import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppSidebar from '@/components/AppSidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, FileDown, RefreshCw, Pencil, Trash2, Battery,
  Truck, Shield, Recycle as RecycleIcon, FileBadge, MapPin, ExternalLink, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ['Planning', 'In Progress', 'Complete'];
const STATUS_STYLE = {
  Planning: 'bg-gray-100 text-gray-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  Complete: 'bg-emerald-100 text-emerald-700',
};

const emptyForm = () => ({
  project_name: '', client_name: '', status: 'Planning', site_location: '',
  asset_ids: [], units_count: 0, estimated_quantity: 0, estimated_quantity_unit: 'kg',
  contractor: { company: '', contact: '', phone: '', email: '' },
  insurance: { insurer: '', policy_no: '', coverage: '', expiry: '' },
  recycler: { facility: '', address: '' },
  permits: [], target_start: '', target_completion: '', notes: '',
});

export default function EolProjectsPage() {
  const navigate = useNavigate();
  const [user] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const isGuest = user?.role === 'guest';
  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [assets, setAssets] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([
        axios.get(`${API}/recycling/projects`, config),
        axios.get(`${API}/recycling/eol-assets`, config),
      ]);
      setProjects(p.data); setAssets(a.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      project_name: p.project_name, client_name: p.client_name, status: p.status,
      site_location: p.site_location || '', asset_ids: p.asset_ids || [],
      units_count: p.units_count || 0, estimated_quantity: p.estimated_quantity || 0,
      estimated_quantity_unit: p.estimated_quantity_unit || 'kg',
      contractor: { ...emptyForm().contractor, ...(p.contractor || {}) },
      insurance: { ...emptyForm().insurance, ...(p.insurance || {}) },
      recycler: { ...emptyForm().recycler, ...(p.recycler || {}) },
      permits: p.permits || [], target_start: p.target_start || '',
      target_completion: p.target_completion || '', notes: p.notes || '',
    });
    setDialogOpen(true);
  };

  const toggleAsset = (id) => {
    setForm((f) => ({ ...f, asset_ids: f.asset_ids.includes(id) ? f.asset_ids.filter((x) => x !== id) : [...f.asset_ids, id] }));
  };
  const addPermit = () => setForm((f) => ({ ...f, permits: [...f.permits, { type: '', number: '', authority: '', expiry: '' }] }));
  const updatePermit = (i, k, v) => setForm((f) => ({ ...f, permits: f.permits.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const removePermit = (i) => setForm((f) => ({ ...f, permits: f.permits.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.project_name || !form.client_name) { toast.error('Project name and client are required'); return; }
    setSaving(true);
    const payload = { ...form, units_count: parseInt(form.units_count) || 0, estimated_quantity: parseFloat(form.estimated_quantity) || 0 };
    try {
      if (editingId) { await axios.put(`${API}/recycling/projects/${editingId}`, payload, config); toast.success('Project updated'); }
      else { const r = await axios.post(`${API}/recycling/projects`, payload, config); toast.success(`Project ${r.data.project_no} created`); }
      setDialogOpen(false); await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await axios.delete(`${API}/recycling/projects/${deleteId}`, config); toast.success('Project deleted'); setDeleteId(null); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const downloadReport = (p) => {
    window.open(`${API}/recycling/projects/${p.id}/report/pdf?auth=${encodeURIComponent(token)}`, '_blank');
    toast.success(`Report for ${p.project_no} opened`);
  };

  const Section = ({ icon: Icon, title, children }) => (
    <div className="border border-gray-100 rounded-lg p-3">
      <div className="text-sm font-semibold text-black flex items-center gap-1.5 mb-2"><Icon className="w-4 h-4 text-gray-500" />{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <AppSidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-black flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <ClipboardList className="w-6 h-6 text-[#0f2e4d]" /> Project Management
              </h1>
              <p className="text-sm text-gray-500">End-of-life project management on behalf of clients · one file per job</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={load} data-testid="projects-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
              {!isGuest && <Button size="sm" className="bg-[#0f2e4d] hover:bg-[#0b2238] text-white" onClick={openCreate} data-testid="new-project-btn"><Plus className="w-4 h-4 mr-1" />New Project</Button>}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {loading ? <div className="flex items-center justify-center h-64 text-gray-400">Loading projects…</div> : (
            projects.length === 0 ? (
              <div className="text-center text-gray-400 py-16 border border-dashed border-gray-200 rounded-xl" data-testid="no-projects">
                <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-40" />No projects yet. Create one to start managing an end-of-life job.
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {projects.map((p) => (
                  <Card key={p.id} className="border-gray-200" data-testid={`project-card-${p.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-gray-400">#{p.project_no}</span>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[p.status] || 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
                          </div>
                          <h3 className="font-bold text-black mt-0.5">{p.project_name}</h3>
                          <p className="text-sm text-gray-500">Client: {p.client_name}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                        <div><div className="text-gray-400 text-xs flex items-center gap-1"><MapPin className="w-3 h-3" />Site</div><div className="text-gray-800">{p.site_location || '—'}</div></div>
                        <div><div className="text-gray-400 text-xs">Units / Qty</div><div className="text-gray-800">{p.units_count} units · {p.estimated_quantity} {p.estimated_quantity_unit}</div></div>
                        <div><div className="text-gray-400 text-xs flex items-center gap-1"><Battery className="w-3 h-3" />Assets in scope</div><div className="text-gray-800">{p.assets?.length || 0}</div></div>
                        <div><div className="text-gray-400 text-xs flex items-center gap-1"><Truck className="w-3 h-3" />Contractor</div><div className="text-gray-800 truncate">{p.contractor?.company || '—'}</div></div>
                        <div><div className="text-gray-400 text-xs flex items-center gap-1"><Shield className="w-3 h-3" />Insurer</div><div className="text-gray-800 truncate">{p.insurance?.insurer || '—'}</div></div>
                        <div><div className="text-gray-400 text-xs flex items-center gap-1"><RecycleIcon className="w-3 h-3" />Recycler</div><div className="text-gray-800 truncate">{p.recycler?.facility || '—'}</div></div>
                      </div>
                      <div className="flex items-center gap-2 mt-4 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => downloadReport(p)} data-testid={`project-report-${p.id}`}><FileDown className="w-4 h-4 mr-1" />Report<ExternalLink className="w-3 h-3 ml-1" /></Button>
                        {!isGuest && <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`project-edit-${p.id}`}><Pencil className="w-4 h-4 mr-1" />Edit</Button>}
                        {!isGuest && <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteId(p.id)} data-testid={`project-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          )}
        </main>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Project' : 'New End-of-Life Project'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Project Name *</Label><Input data-testid="proj-name" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} /></div>
              <div><Label>Client *</Label><Input data-testid="proj-client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="proj-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Units Being Moved</Label><Input data-testid="proj-units" type="number" value={form.units_count} onChange={(e) => setForm({ ...form, units_count: e.target.value })} /></div>
              <div><Label>Est. Quantity (kg)</Label><Input data-testid="proj-qty" type="number" value={form.estimated_quantity} onChange={(e) => setForm({ ...form, estimated_quantity: e.target.value })} /></div>
            </div>
            <div><Label>Site / Location</Label><Input data-testid="proj-site" value={form.site_location} onChange={(e) => setForm({ ...form, site_location: e.target.value })} placeholder="Auto-filled from first selected asset if blank" /></div>

            <Section icon={Battery} title="Assets in Scope (from fleet)">
              <div className="max-h-40 overflow-y-auto space-y-1">
                {assets.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 rounded hover:bg-gray-50" data-testid={`proj-asset-${a.id}`}>
                    <input type="checkbox" checked={form.asset_ids.includes(a.id)} onChange={() => toggleAsset(a.id)} />
                    <span className="text-gray-800">{a.name}</span>
                    <span className="text-xs text-gray-400">{a.chemistry} · SOH {a.soh}% · {a.location}</span>
                  </label>
                ))}
              </div>
            </Section>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Target Start</Label><Input data-testid="proj-start" type="date" value={form.target_start} onChange={(e) => setForm({ ...form, target_start: e.target.value })} /></div>
              <div><Label>Target Completion</Label><Input data-testid="proj-completion" type="date" value={form.target_completion} onChange={(e) => setForm({ ...form, target_completion: e.target.value })} /></div>
            </div>

            <Section icon={Truck} title="Contractor">
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Company" data-testid="proj-contractor-company" value={form.contractor.company} onChange={(e) => setForm({ ...form, contractor: { ...form.contractor, company: e.target.value } })} />
                <Input placeholder="Contact name" value={form.contractor.contact} onChange={(e) => setForm({ ...form, contractor: { ...form.contractor, contact: e.target.value } })} />
                <Input placeholder="Phone" value={form.contractor.phone} onChange={(e) => setForm({ ...form, contractor: { ...form.contractor, phone: e.target.value } })} />
                <Input placeholder="Email" value={form.contractor.email} onChange={(e) => setForm({ ...form, contractor: { ...form.contractor, email: e.target.value } })} />
              </div>
            </Section>

            <Section icon={Shield} title="Insurance">
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Insurer" data-testid="proj-insurer" value={form.insurance.insurer} onChange={(e) => setForm({ ...form, insurance: { ...form.insurance, insurer: e.target.value } })} />
                <Input placeholder="Policy #" value={form.insurance.policy_no} onChange={(e) => setForm({ ...form, insurance: { ...form.insurance, policy_no: e.target.value } })} />
                <Input placeholder="Coverage" value={form.insurance.coverage} onChange={(e) => setForm({ ...form, insurance: { ...form.insurance, coverage: e.target.value } })} />
                <Input placeholder="Expiry" type="date" value={form.insurance.expiry} onChange={(e) => setForm({ ...form, insurance: { ...form.insurance, expiry: e.target.value } })} />
              </div>
            </Section>

            <Section icon={RecycleIcon} title="Recycler">
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Facility" data-testid="proj-recycler" value={form.recycler.facility} onChange={(e) => setForm({ ...form, recycler: { ...form.recycler, facility: e.target.value } })} />
                <Input placeholder="Address" value={form.recycler.address} onChange={(e) => setForm({ ...form, recycler: { ...form.recycler, address: e.target.value } })} />
              </div>
            </Section>

            <Section icon={FileBadge} title="Permits & Approvals">
              {form.permits.map((pm, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-4" placeholder="Type" value={pm.type} onChange={(e) => updatePermit(i, 'type', e.target.value)} />
                  <Input className="col-span-3" placeholder="Number" value={pm.number} onChange={(e) => updatePermit(i, 'number', e.target.value)} />
                  <Input className="col-span-3" placeholder="Authority" value={pm.authority} onChange={(e) => updatePermit(i, 'authority', e.target.value)} />
                  <Input className="col-span-1" type="date" value={pm.expiry} onChange={(e) => updatePermit(i, 'expiry', e.target.value)} />
                  <button className="col-span-1 text-gray-400 hover:text-red-600" onClick={() => removePermit(i)}><X className="w-4 h-4" /></button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addPermit} data-testid="proj-add-permit"><Plus className="w-4 h-4 mr-1" />Add Permit</Button>
            </Section>

            <div><Label>Notes &amp; Considerations</Label><Textarea data-testid="proj-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Site access, isolation, PPE, routing, interim storage, etc." /></div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="bg-[#0f2e4d] hover:bg-[#0b2238] text-white" data-testid="save-project-btn">{saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Create Project')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete project?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the project file. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDelete} data-testid="confirm-delete-project">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
